"use client"

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, X, Globe, ChevronDown, Check, Linkedin } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Language, getTranslation } from '@/lib/translations';
import { motion, AnimatePresence } from 'framer-motion';

interface LegalHeaderProps {
  lang: Language;
  scrolled: boolean;
  currentPage: 'aviso-legal' | 'politica-privacidad' | 'politica-cookies' | 'news';
  /** Ruta tras el prefijo de idioma para el selector de idioma (p. ej. `news/123`). */
  langSwitchPath?: string;
}

export default function LegalHeader({ lang, currentPage, langSwitchPath }: LegalHeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const router = useRouter();
  const t = getTranslation(lang);

  // Combined mobile menu state management: body scroll lock + ESC key handler
  useEffect(() => {
    // Body scroll lock
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    // ESC key handler
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileOpen) {
        setMobileOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)

    // Cleanup on unmount or mobileOpen change
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleEscape)
    }
  }, [mobileOpen])

  // Aggressive cleanup on unmount to prevent stuck states
  useEffect(() => {
    return () => {
      setMobileOpen(false)
      document.body.style.overflow = ''
    }
  }, [])

  const getCurrentLangPath = () => {
    switch(lang) {
      case 'es': return '/es'
      case 'gl': return '/gal'
      case 'en': return '/en'
      default: return '/es'
    }
  };

  const getLangPagePath = (targetLang: 'es' | 'gl' | 'en') => {
    const langPrefix = targetLang === 'gl' ? '/gal' : `/${targetLang}`;
    if (langSwitchPath) return `${langPrefix}/${langSwitchPath}`;
    return currentPage === 'news' ? `${langPrefix}/news` : `${langPrefix}/legal/${currentPage}`;
  };

  const scrollToSection = (id: string) => {
    // Navegar a la raíz con el idioma
    router.push(`${getCurrentLangPath()}/#${id}`);
  };

  return (
    <>
      <header
        ref={navRef}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md shadow-md"
      >
      <div className="px-4 md:px-6 lg:px-16 xl:px-32 mt-1">
        <div className="flex h-20 items-center justify-between">
          {/* Izquierda: Logo (siempre) + Nav (solo desktop) */}
          <div className="flex items-center gap-4 lg:gap-8">
            <Link href={getCurrentLangPath()} className="flex items-center space-x-2 group ml-1 flex-shrink-0">
              <Image
                src="/display-azul.png"
                alt="EGS Logo"
                width={150}
                height={30}
                className="transition-transform group-hover:scale-110 h-[25px] w-auto"
                priority
              />
            </Link>

            {/* Navigation Desktop */}
            <nav className="hidden md:flex items-center">
              <div className="flex items-center space-x-4 lg:space-x-8">
                {["nuestra-solucion", "que-sucede", "conocenos", "unete"].map((id) => (
                  <button
                    key={id}
                    className="cursor-pointer font-medium text-[15px] transition-all hover:scale-105 hover:text-cyan-600"
                    onClick={() => scrollToSection(id)}
                  >
                    {id === "que-sucede"
                      ? t.nav.whatHappens
                      : id === "nuestra-solucion"
                      ? t.nav.ourSolution
                      : id === "conocenos"
                      ? t.nav.meetUs
                      : t.nav.join}
                  </button>
                ))}
                <Link
                  href={`${getCurrentLangPath()}/news`}
                  aria-current={currentPage === 'news' ? 'page' : undefined}
                  className={`cursor-pointer font-medium text-[15px] transition-all hover:scale-105 hover:text-cyan-600 ${
                    currentPage === 'news' ? 'scale-105 font-semibold text-cyan-600' : ''
                  }`}
                >
                  {t.nav.news}
                </Link>
              </div>
            </nav>
          </div>{/* end izquierda */}

          {/* LinkedIn, Language Switcher & Login Button (Desktop) */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-4">
            <a
              href="https://www.linkedin.com/company/egswater"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer font-semibold transition-all border-cyan-600 text-cyan-600 hover:bg-cyan-600 hover:text-white"
              >
                <Linkedin className="h-4 w-4" />
              </Button>
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="cursor-pointer font-medium transition-all gap-2 text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 border-0"
                >
                  <Globe className="w-4 h-4" />
                  <span className="uppercase">{lang === 'gl' ? 'GL' : lang}</span>
                  <ChevronDown className="w-3 h-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[160px] bg-white/95 backdrop-blur-md border-gray-200"
              >
                <DropdownMenuItem asChild>
                  <Link
                    href={getLangPagePath('es')}
                    className={`
                      w-full cursor-pointer flex items-center justify-between
                      px-2 py-1.5 text-sm outline-none transition-colors
                      hover:bg-cyan-600 hover:text-white
                      focus:bg-cyan-600 focus:text-white
                      ${lang === 'es' ? 'font-semibold text-cyan-600' : ''}
                    `}
                  >
                    <span>ES - Español</span>
                    {lang === 'es' && <Check className="w-4 h-4 text-current" />}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    href={getLangPagePath('gl')}
                    className={`
                      w-full cursor-pointer flex items-center justify-between
                      px-2 py-1.5 text-sm outline-none transition-colors
                      hover:bg-cyan-600 hover:text-white
                      focus:bg-cyan-600 focus:text-white
                      ${lang === 'gl' ? 'font-semibold text-cyan-600' : ''}
                    `}
                  >
                    <span>GL - Galego</span>
                    {lang === 'gl' && <Check className="w-4 h-4 text-current" />}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    href={getLangPagePath('en')}
                    className={`
                      w-full cursor-pointer flex items-center justify-between
                      px-2 py-1.5 text-sm outline-none transition-colors
                      hover:bg-cyan-600 hover:text-white
                      focus:bg-cyan-600 focus:text-white
                      ${lang === 'en' ? 'font-semibold text-cyan-600' : ''}
                    `}
                  >
                    <span>EN - English</span>
                    {lang === 'en' && <Check className="w-4 h-4 text-current" />}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={() => scrollToSection("unete")}
              className="h-9 text-[15px] font-semibold px-4 rounded-md transition-all bg-cyan-600 text-white hover:bg-cyan-700"
            >
              {t.nav.startFree}
            </button>
            <Link href={`/${lang === 'gl' ? 'gal' : lang}/iniciar-sesion`}>
              <Button
                variant="outline"
                className="h-9 px-3 lg:px-4 cursor-pointer font-semibold text-[15px] transition-all border-cyan-600 text-cyan-600 hover:bg-cyan-600 hover:text-white"
              >
                {t.nav.login}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="default"
            size="sm"
            className="cursor-pointer md:hidden bg-white text-cyan-700 hover:bg-cyan-600 hover:text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>
    </header>

    {/* Mobile Menu - Full Screen Overlay */}
    <AnimatePresence key={`mobile-menu-${lang}`}>
      {mobileOpen && (
        <motion.div
          key="mobile-menu-container"
          className="fixed inset-0 z-[9998] md:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <motion.div
            key="mobile-menu-backdrop"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />

          {/* Full Screen Menu Panel */}
          <motion.div
            key="mobile-menu-panel"
            className="absolute inset-0 bg-cyan-600 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            {/* Close Button */}
            <div className="flex justify-end p-6">
              <button
                onClick={() => setMobileOpen(false)}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label="Cerrar menú"
              >
                <X className="h-8 w-8" />
              </button>
            </div>

            {/* Navigation Items - Centered */}
            <div className="flex-1 flex flex-col justify-center items-center px-8 space-y-8">
              {["nuestra-solucion", "que-sucede", "conocenos", "unete"].map((id) => (
                <button
                  key={id}
                  className="text-white hover:text-gray-200 transition-colors text-3xl font-semibold text-center w-full"
                  onClick={() => {
                    scrollToSection(id);
                    setMobileOpen(false);
                  }}
                >
                  {id === "que-sucede"
                    ? t.nav.whatHappens
                    : id === "nuestra-solucion"
                    ? t.nav.ourSolution
                    : id === "conocenos"
                    ? t.nav.meetUs
                    : t.nav.join}
                </button>
              ))}
              <Link
                href={`${getCurrentLangPath()}/news`}
                className="text-white hover:text-gray-200 transition-colors text-3xl font-semibold text-center w-full"
                onClick={() => setMobileOpen(false)}
              >
                {t.nav.news}
              </Link>
            </div>

            {/* Bottom Section - LinkedIn + Language + Login */}
            <div className="flex flex-col px-8">
              {/* LinkedIn Button - Centered with equal spacing */}
              <div className="py-8">
                <a
                  href="https://www.linkedin.com/company/egswater"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                >
                  <Button className="w-full cursor-pointer bg-white text-cyan-600 hover:bg-gray-100 text-lg py-6 font-semibold flex items-center justify-center gap-2">
                    <Linkedin className="h-5 w-5" />
                    LinkedIn
                  </Button>
                </a>
              </div>

              {/* Language Switcher & Login */}
              <div className="space-y-4 pb-8">
                {/* Language Switcher */}
                <div className="flex items-center justify-center gap-3 text-base">
                  <Globe className="w-4 h-4 text-white" />
                  <Link
                    href={getLangPagePath('es')}
                    className={`transition-all ${
                      lang === 'es'
                        ? "text-white font-bold"
                        : "text-white/70 hover:text-white"
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    ES
                  </Link>
                  <span className="text-white/50">|</span>
                  <Link
                    href={getLangPagePath('gl')}
                    className={`transition-all ${
                      lang === 'gl'
                        ? "text-white font-bold"
                        : "text-white/70 hover:text-white"
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    GL
                  </Link>
                  <span className="text-white/50">|</span>
                  <Link
                    href={getLangPagePath('en')}
                    className={`transition-all ${
                      lang === 'en'
                        ? "text-white font-bold"
                        : "text-white/70 hover:text-white"
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    EN
                  </Link>
                </div>

                {/* Start Free Button */}
                <button
                  onClick={() => { scrollToSection("unete"); setMobileOpen(false); }}
                  className="w-full bg-white text-cyan-600 hover:bg-gray-100 text-lg py-3 font-semibold rounded-md transition-all"
                >
                  {t.nav.startFree}
                </button>

                {/* Login Button */}
                <Link href={`/${lang === 'gl' ? 'gal' : lang}/iniciar-sesion`} onClick={() => setMobileOpen(false)}>
                  <Button className="w-full cursor-pointer bg-white text-cyan-600 hover:bg-gray-100 text-lg py-6 font-semibold">
                    {t.nav.login}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}
